/**
 * NewJob wizard — cyberpunk redesign.
 *
 * Source-of-truth for amount / dwell bounds: `DEFAULT_POLICY` in
 * `packages/shared/src/config.ts` (ported from the original CLI
 * wizard's policy defaults). We import and derive the frontend
 * constants here so the Worker's `/api/jobs` validation and the
 * wizard's client-side validation cannot drift.
 *
 * Previous iterations:
 *   - Personal-use flow (no per-job password, server-side mnemonic
 *     generation + master-key AES-GCM envelope). See PR
 *     `refactor/personal-use-flow-and-splits-fees`.
 *   - Splits-aware fee estimator. Same PR.
 *
 * This revision adds:
 *   - Always-visible per-hop fee table (`<table class="cp-fee-table">`)
 *     with Hop / Description / USDT / % columns.
 *   - Alignment to CLI policy defaults for amount (2,000–30,000 USDT,
 *     step 100) and dwell (7–21 days).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clipboard, Zap } from "lucide-react";

import type { CreateJobRequest, FeeBreakdown } from "@u2u/shared";
import { formatDwellDuration, DEFAULT_POLICY } from "@u2u/shared";

import { createJob, estimateFees, getJob } from "../lib/api.js";
import { DepositModal } from "../components/DepositModal.js";

// ── Policy-derived constants (single source of truth: DEFAULT_POLICY) ───
const MIN_AMOUNT = DEFAULT_POLICY.amounts.min_usdt; // 2_000
const MAX_AMOUNT = DEFAULT_POLICY.amounts.max_usdt; // 30_000
const AMOUNT_STEP = 100;
const MIN_DWELL_DAYS = Math.floor(DEFAULT_POLICY.dwell_time.min_seconds / 86_400); // 7
const MAX_DWELL_DAYS = Math.floor(DEFAULT_POLICY.dwell_time.max_seconds / 86_400); // 21

/** Amount chips — each value must sit inside [MIN_AMOUNT, MAX_AMOUNT]. */
const AMOUNT_CHIPS = [2_000, 5_000, 10_000, 20_000, 30_000] as const;
/** Dwell presets — each value must sit inside [MIN_DWELL_DAYS, MAX_DWELL_DAYS]. */
const DWELL_PRESETS = [7, 10, 14, 18, 21] as const;
const SPLITS = [1, 2, 3, 4, 5] as const;

type DwellMode = (typeof DWELL_PRESETS)[number] | "custom";

/** Cheap TRON base58 sanity check — the Worker does real validation. */
function looksLikeTronAddress(addr: string): boolean {
  if (!addr) return false;
  if (addr.length !== 34) return false;
  if (!addr.startsWith("T")) return false;
  return /^[1-9A-HJ-NP-Za-km-z]+$/.test(addr);
}

export function NewJob() {
  const nav = useNavigate();

  // form state
  const [amount, setAmount] = useState<string>("5000");
  const [dwellMode, setDwellMode] = useState<DwellMode>(14);
  const [dwellCustom, setDwellCustom] = useState<string>("");
  const [numSplits, setNumSplits] = useState<number>(1);
  const [destinations, setDestinations] = useState<string[]>([""]);

  // fee / submit state
  const [fees, setFees] = useState<FeeBreakdown | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /**
   * After a successful POST /api/jobs we immediately fetch the job row
   * (so we can read wallet A's TRON address without widening the
   * CreateJobResponse schema) and open <DepositModal/>. The previous
   * personal-use flow also surfaced a one-time mnemonic-backup modal
   * here — that step was removed because in this flow wallets A + B are
   * ephemeral; the server-side AES-GCM blob is sufficient, and
   * operators can break-glass via GET /api/jobs/:token/secrets if ever
   * needed.
   */
  const [deposit, setDeposit] = useState<{
    token: string;
    walletAddress: string;
    amountUsdt: number;
  } | null>(null);

  const amountNum = useMemo(() => {
    const n = parseFloat(amount);
    return Number.isFinite(n) ? n : NaN;
  }, [amount]);

  const dwellDays = useMemo(() => {
    if (dwellMode === "custom") {
      const n = parseInt(dwellCustom, 10);
      return Number.isFinite(n) ? n : NaN;
    }
    return dwellMode;
  }, [dwellMode, dwellCustom]);

  const dwellSeconds = useMemo(
    () => (Number.isFinite(dwellDays) ? (dwellDays as number) * 86_400 : 0),
    [dwellDays],
  );

  // keep destinations array length in sync with numSplits
  useEffect(() => {
    setDestinations((ds) => {
      const out = ds.slice(0, numSplits);
      while (out.length < numSplits) out.push("");
      return out;
    });
  }, [numSplits]);

  // live fee estimate — debounced so rapid amount/splits changes don't
  // pound the Worker. Re-fires whenever either input changes.
  const debounceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!Number.isFinite(amountNum) || amountNum <= 0) return;
    const ctrl = new AbortController();
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      estimateFees({ amountUsdt: amountNum, numSplits })
        .then((f) => {
          if (!ctrl.signal.aborted) setFees(f);
        })
        .catch(() => {
          if (!ctrl.signal.aborted) setFees(null);
        });
    }, 180);
    return () => {
      ctrl.abort();
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [amountNum, numSplits]);

  const destValid = destinations.map((d) =>
    d.length === 0 ? null : looksLikeTronAddress(d),
  );

  const amountOk =
    Number.isFinite(amountNum) && amountNum >= MIN_AMOUNT && amountNum <= MAX_AMOUNT;
  const dwellOk =
    Number.isFinite(dwellDays) &&
    (dwellDays as number) >= MIN_DWELL_DAYS &&
    (dwellDays as number) <= MAX_DWELL_DAYS;
  const destsOk =
    destinations.length === numSplits &&
    destinations.every((d) => looksLikeTronAddress(d));

  const canSubmit = !submitting && amountOk && dwellOk && destsOk;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const body: CreateJobRequest = {
        amount_usdt: amountNum,
        dwell_seconds: dwellSeconds,
        num_splits: numSplits,
        destinations,
      };
      const res = await createJob(body);
      // NB: the response still carries wallet_a_mnemonic / wallet_b_mnemonic
      // for backward-compat; we deliberately ignore them in the UI.
      // Operators can retrieve plaintext phrases via
      // GET /api/jobs/:token/secrets (bearer-auth with the master key).
      try {
        const j = await getJob(res.token);
        setDeposit({
          token: res.token,
          walletAddress: j.job.source_tron_address,
          amountUsdt: j.job.amount_usdt,
        });
      } catch {
        // If the job fetch fails (rare — Worker just created it), fall
        // through to the progress page which will retry and surface a
        // prompt itself.
        nav(`/job/${res.token}`);
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function pasteInto(idx: number) {
    try {
      const txt = (await navigator.clipboard.readText()).trim();
      setDestinations((arr) =>
        arr.map((v, k) => (k === idx ? txt : v)),
      );
    } catch {
      /* clipboard permission can be denied; user can still type */
    }
  }

  return (
    <div className="cp-page">
      <div className="cp-shell">
        <div className="cp-row-between">
          <h1 className="cp-h1">// new job</h1>
          <button
            type="button"
            className="cp-btn cp-btn--small"
            onClick={() => nav("/")}
          >
            cancel
          </button>
        </div>

        <form onSubmit={submit} className="cp-frame" noValidate>
          <span className="cp-corner-tr" />
          <span className="cp-corner-bl" />

          <div className="cp-stack">
            {/* --- amount --- */}
            <div className="cp-field">
              <label className="cp-label" htmlFor="amount">
                Amount
              </label>
              <div className="cp-input-wrap">
                <input
                  id="amount"
                  className="cp-input"
                  type="number"
                  inputMode="decimal"
                  min={MIN_AMOUNT}
                  max={MAX_AMOUNT}
                  step={AMOUNT_STEP}
                  placeholder="0"
                  value={amount}
                  aria-invalid={amount.length > 0 && !amountOk}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <span className="cp-suffix">USDT</span>
              </div>
              <div className="cp-chips">
                {AMOUNT_CHIPS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    className="cp-chip"
                    onClick={() => setAmount(String(v))}
                  >
                    {v.toLocaleString()}
                  </button>
                ))}
              </div>
              <p className="cp-hint">
                Valid range: {MIN_AMOUNT.toLocaleString()}–
                {MAX_AMOUNT.toLocaleString()} USDT (step{" "}
                {AMOUNT_STEP.toLocaleString()}).
              </p>
              {amount.length > 0 && !amountOk && (
                <p className="cp-error">
                  Amount must be between {MIN_AMOUNT.toLocaleString()} and{" "}
                  {MAX_AMOUNT.toLocaleString()} USDT
                </p>
              )}
            </div>

            {/* --- dwell --- */}
            <div className="cp-field">
              <label className="cp-label" htmlFor="dwell">
                Dwell time
              </label>
              <select
                id="dwell"
                className="cp-select"
                value={String(dwellMode)}
                onChange={(e) => {
                  const v = e.target.value;
                  setDwellMode(v === "custom" ? "custom" : (parseInt(v, 10) as DwellMode));
                }}
              >
                {DWELL_PRESETS.map((d) => (
                  <option key={d} value={d}>
                    {d} days
                  </option>
                ))}
                <option value="custom">Custom…</option>
              </select>
              {dwellMode === "custom" && (
                <div style={{ marginTop: 10 }}>
                  <input
                    className="cp-input"
                    type="number"
                    inputMode="numeric"
                    min={MIN_DWELL_DAYS}
                    max={MAX_DWELL_DAYS}
                    step={1}
                    placeholder={`${MIN_DWELL_DAYS}–${MAX_DWELL_DAYS}`}
                    value={dwellCustom}
                    aria-invalid={dwellCustom.length > 0 && !dwellOk}
                    onChange={(e) => setDwellCustom(e.target.value)}
                  />
                  <p className="cp-hint">
                    Valid range: {MIN_DWELL_DAYS}–{MAX_DWELL_DAYS} days
                  </p>
                </div>
              )}
              {dwellOk && dwellMode !== "custom" && (
                <p className="cp-hint">
                  Funds will shield for {formatDwellDuration(dwellSeconds)} before
                  fan-out begins.
                </p>
              )}
            </div>

            {/* --- splits --- */}
            <div className="cp-field">
              <label className="cp-label">Number of splits</label>
              <div className="cp-pills" role="radiogroup" aria-label="splits">
                {SPLITS.map((n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    className="cp-pill"
                    aria-pressed={numSplits === n}
                    aria-checked={numSplits === n}
                    onClick={() => setNumSplits(n)}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="cp-hint">
                Funds are fanned out across {numSplits} destination
                {numSplits > 1 ? "s" : ""}. More splits ={" "}
                <strong>more gas + THORChain outbound fees</strong> (one
                extra unshield + outbound per extra leg).
              </p>
            </div>

            {/* --- destinations --- */}
            <div className="cp-field">
              <label className="cp-label">Destinations</label>
              {destinations.map((dest, i) => (
                <div key={i} className="cp-dest">
                  <label className="cp-label" htmlFor={`dest-${i}`}>
                    Destination #{i + 1}
                  </label>
                  <div className="cp-input-row">
                    <input
                      id={`dest-${i}`}
                      className="cp-input"
                      placeholder="T…"
                      spellCheck={false}
                      autoComplete="off"
                      autoCapitalize="none"
                      value={dest}
                      aria-invalid={destValid[i] === false}
                      onChange={(e) =>
                        setDestinations((arr) =>
                          arr.map((v, k) => (k === i ? e.target.value.trim() : v)),
                        )
                      }
                    />
                    <button
                      type="button"
                      className="cp-btn cp-btn--small"
                      onClick={() => pasteInto(i)}
                      title="Paste from clipboard"
                    >
                      <Clipboard size={12} /> paste
                    </button>
                  </div>
                  {destValid[i] === false && (
                    <p className="cp-error">Invalid TRON address (expected T + 33 base58 chars)</p>
                  )}
                  {destValid[i] === true && <p className="cp-ok">Looks valid</p>}
                </div>
              ))}
            </div>

            {/* --- fee table --- */}
            {fees && <FeeTable f={fees} amountUsdt={amountNum} />}

            {err && (
              <p className="cp-error" role="alert">
                {err}
              </p>
            )}

            <button
              type="submit"
              className="cp-btn cp-btn--primary cp-btn--block"
              disabled={!canSubmit}
            >
              {submitting ? (
                <>
                  <span className="cp-spinner" /> Generating wallets + submitting…
                </>
              ) : (
                <>
                  <Zap size={14} /> Launch job
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {deposit && (
        <DepositModal
          walletAddress={deposit.walletAddress}
          amountUsdt={deposit.amountUsdt}
          onDeposited={() => nav(`/job/${deposit.token}`)}
          onDismiss={() => nav(`/job/${deposit.token}`)}
        />
      )}
    </div>
  );
}

// ── Fee breakdown table ───────────────────────────────────────────────────
//
// 7 rows (Skim + 6 hops) → always visible. Hops 4 and 5 both map to the
// per-leg Railgun unshield / swap cluster in the fee model (see fees.ts
// — `hop5_cross_contract_gas` is gas, `hop5_uniswap_pool_fee` is the
// pool fee on the subsequent Uniswap hop). Hop 6 sums THORChain outbound
// fee + slip + router gas since they're always reported together.

type FeeRow = {
  hop: string;
  description: string;
  usdt: number;
};

function FeeTable({ f, amountUsdt }: { f: FeeBreakdown; amountUsdt: number }) {
  const N = f.num_splits;
  const rows: FeeRow[] = [
    { hop: "Head", description: "5% skim to treasury", usdt: f.skim_usdt },
    {
      hop: "1",
      description: "TRON USDT → THORChain → ETH (inbound fee + slip)",
      usdt: f.hop1_thorchain_out + f.hop1_thorchain_slip,
    },
    {
      hop: "2",
      description: "Wallet A Uniswap ETH → USDC (gas + pool)",
      usdt: f.hop2_uniswap_gas + f.hop2_uniswap_pool_fee,
    },
    {
      hop: "3",
      description: "Railgun shield (gas)",
      usdt: f.hop3_shield_gas,
    },
    {
      hop: "4",
      description: `Railgun cross-contract unshield gas (×${N})`,
      usdt: f.hop5_cross_contract_gas,
    },
    {
      hop: "5",
      description: `Uniswap swap pool fee on wallet B legs (×${N})`,
      usdt: f.hop5_uniswap_pool_fee,
    },
    {
      hop: "6",
      description: `Wallet B → THORChain → TRON (×${N}, fee+slip+router gas)`,
      usdt: f.hop6_thorchain_out + f.hop6_thorchain_slip + f.hop6_router_gas,
    },
  ];
  const pct = (v: number) =>
    amountUsdt > 0 ? (v / amountUsdt) * 100 : 0;
  return (
    <div className="cp-fee-table-wrap" role="region" aria-label="Fee breakdown">
      <div className="cp-fee-table-title">// fee breakdown</div>
      <table className="cp-fee-table">
        <thead>
          <tr>
            <th scope="col" className="cp-fee-th-hop">Hop</th>
            <th scope="col" className="cp-fee-th-desc">Description</th>
            <th scope="col" className="cp-fee-th-num">USDT</th>
            <th scope="col" className="cp-fee-th-num">% of amt</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.hop}>
              <td className="cp-fee-td-hop">{r.hop}</td>
              <td className="cp-fee-td-desc">{r.description}</td>
              <td className="cp-fee-td-num">{r.usdt.toFixed(2)}</td>
              <td className="cp-fee-td-num cp-fee-td-pct">
                {pct(r.usdt).toFixed(2)}%
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="cp-fee-total-row">
            <td className="cp-fee-td-hop">Total</td>
            <td className="cp-fee-td-desc">
              All fees combined (splits: {N})
            </td>
            <td className="cp-fee-td-num">{f.total_fees.toFixed(2)}</td>
            <td className="cp-fee-td-num cp-fee-td-pct">
              {f.total_fee_pct.toFixed(2)}%
            </td>
          </tr>
          <tr className="cp-fee-final-row">
            <td className="cp-fee-td-hop">Final</td>
            <td className="cp-fee-td-desc">Expected delivered to destinations</td>
            <td className="cp-fee-td-num cp-fee-td-final">
              {f.expected_final_usdt.toFixed(2)}
            </td>
            <td className="cp-fee-td-num cp-fee-td-pct">
              {pct(f.expected_final_usdt).toFixed(2)}%
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
